import { Bug, Keyboard, LogOut, MessageSquareText, Settings } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { CommandPalette } from '@/components/CommandPalette';
import { AiChatPanel } from '@/components/ai/AiChatPanel';
import { FeedbackButton } from '@/components/feedback/FeedbackButton';
import { OrganizationSettingsModal } from '@/components/organization/OrganizationSettingsModal';
import { KeyboardShortcutsHelp } from './KeyboardShortcutsHelp';
import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';
import checkIcon from './icons/check.svg';
import { useBrowserSessionRefresh } from '@/hooks/use-browser-session-refresh';
import { useKeyboardShortcuts } from '@/hooks/use-keyboard-shortcuts';
import { useSessionProfileSync } from '@/hooks/use-session-profile-sync';
import { extractTenantOrganizationSettings, useTenantSettings } from '@/hooks/use-tenant-settings';
import { api } from '@/lib/api';
import {
  LOANS_APP_ROLE_QUERY_KEY,
  isLoansAppRole,
  resolveCurrentAppRole,
  resolveLoansAppRole,
  setStoredLoansAppRole,
} from '@/lib/app-role';
import { getAppSelectorInfo } from '@/lib/app-selector-info';
import { queryClient } from '@/lib/query-client';
import {
  resolveActiveTenantId,
  resolveAvailableTenants,
  setStoredActiveTenantId,
} from '@/lib/tenant-context';
import { useAuthStore } from '@/stores/auth-store';
import type { User } from '@/types';

const REGISTER_QUERY_PARAM = 'register_id';

export function Wrapper() {
  const user = useAuthStore(s => s.user);
  const setUser = useAuthStore(s => s.setUser);
  const logout = useAuthStore(s => s.logout);
  useSessionProfileSync();
  useBrowserSessionRefresh();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showShortcutsHelp, setShowShortcutsHelp] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [organizationSettingsOpen, setOrganizationSettingsOpen] = useState(false);
  const bootId = useMemo(() => crypto.randomUUID(), []);
  const hasSyncedRegisterParamRef = useRef(false);
  const roleSyncRequestRef = useRef<string | null>(null);
  const availableTenants = useMemo(() => resolveAvailableTenants(user), [user]);
  const activeTenantId = useMemo(() => resolveActiveTenantId(user), [user]);
  const activeTenant = useMemo(
    () =>
      availableTenants.find(tenant => tenant.id === activeTenantId) ?? availableTenants[0] ?? null,
    [activeTenantId, availableTenants],
  );
  const { data: tenantSettings } = useTenantSettings(activeTenantId);
  const organizationSettings = useMemo(
    () =>
      extractTenantOrganizationSettings(
        tenantSettings?.settings,
        activeTenant?.name ?? '',
        tenantSettings?.tenant,
      ),
    [activeTenant?.name, tenantSettings?.settings, tenantSettings?.tenant],
  );
  const appSelectorInfo = useMemo(() => getAppSelectorInfo(user), [user]);

  const profileOptions = useMemo(
    () => [
      { label: 'Shortcuts', onClick: () => setShowShortcutsHelp(true), icon: Keyboard },
      {
        label: 'Feature request',
        onClick: () => window.dispatchEvent(new CustomEvent('app:open-feature-request')),
        icon: MessageSquareText,
      },
      {
        label: 'Report a bug',
        onClick: () => window.dispatchEvent(new CustomEvent('app:open-bug-report')),
        icon: Bug,
      },
      { label: 'Sign out', onClick: () => void logout(), icon: LogOut, danger: true },
    ],
    [logout],
  );

  const updateRegisterQueryParam = useCallback(
    (tenantId: string) => {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.set(REGISTER_QUERY_PARAM, tenantId);
      setSearchParams(nextParams, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  useEffect(() => {
    const requestedAppRole = searchParams.get(LOANS_APP_ROLE_QUERY_KEY);
    if (!user || !isLoansAppRole(requestedAppRole)) {
      return;
    }

    const currentAppRole = resolveCurrentAppRole(user);
    if (requestedAppRole === currentAppRole) {
      if (roleSyncRequestRef.current === requestedAppRole) {
        roleSyncRequestRef.current = null;
      }
      return;
    }

    if (roleSyncRequestRef.current === requestedAppRole) {
      return;
    }

    roleSyncRequestRef.current = requestedAppRole;
    setStoredLoansAppRole(requestedAppRole);

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete(REGISTER_QUERY_PARAM);
    setSearchParams(nextParams, { replace: true });

    void api
      .get<User>('/auth/me', {
        headers: {
          'X-App-Role': requestedAppRole,
        },
        skipTenantContext: true,
      } as any)
      .then(response => {
        setUser(response.data);
      })
      .catch((error: any) => {
        if (error?.response?.status === 401) {
          void logout(false);
        }
      })
      .finally(() => {
        roleSyncRequestRef.current = null;
      });
  }, [logout, searchParams, setSearchParams, setUser, user]);

  useEffect(() => {
    if (!user || !availableTenants.length) {
      return;
    }

    const registerIdFromQuery = searchParams.get(REGISTER_QUERY_PARAM);
    const isRegisterIdAllowed = registerIdFromQuery
      ? availableTenants.some(tenant => tenant.id === registerIdFromQuery)
      : false;

    if (!hasSyncedRegisterParamRef.current) {
      hasSyncedRegisterParamRef.current = true;

      if (registerIdFromQuery && isRegisterIdAllowed && registerIdFromQuery !== activeTenantId) {
        setStoredActiveTenantId(registerIdFromQuery, {
          ...user,
          availableTenants,
        });
        setUser({
          ...user,
          tenantId: registerIdFromQuery,
          availableTenants,
        });
        return;
      }
    }

    if (activeTenantId && registerIdFromQuery !== activeTenantId) {
      updateRegisterQueryParam(activeTenantId);
    }
  }, [activeTenantId, availableTenants, searchParams, setUser, updateRegisterQueryParam, user]);

  const handleTenantChange = useCallback(
    async (tenantId: string) => {
      if (!user || tenantId === activeTenantId) {
        return;
      }

      // An application belongs to the currently selected business. Keep an
      // application detail or proposal-calculator URL from refetching against
      // the newly selected business, where it would correctly return "not
      // found" but leave the user stranded.
      const isApplicationDetail =
        location.pathname.startsWith('/origination/') && location.pathname !== '/origination/new';
      const isProposalCalculator =
        location.pathname === '/calculator' &&
        new URLSearchParams(location.search).has('applicationId');
      if (isApplicationDetail || isProposalCalculator) {
        navigate('/origination', { replace: true });
      }

      setStoredActiveTenantId(tenantId, {
        ...user,
        availableTenants,
      });
      setUser({
        ...user,
        tenantId,
        availableTenants,
      });
      updateRegisterQueryParam(tenantId);

      try {
        const response = await api.get<User>('/auth/me', {
          headers: {
            'X-Tenant-Id': tenantId,
            'X-App-Role': resolveLoansAppRole({
              ...user,
              tenantId,
              availableTenants,
            }),
          },
        });
        setUser(response.data);
      } catch (error: any) {
        if (error?.response?.status === 401) {
          void logout(false);
          return;
        }
      }

      await queryClient.cancelQueries();
      void queryClient.invalidateQueries();
    },
    [
      activeTenantId,
      availableTenants,
      location.pathname,
      location.search,
      logout,
      navigate,
      setUser,
      updateRegisterQueryParam,
      user,
    ],
  );

  const headerMenuItems = useMemo(
    () =>
      availableTenants.map(tenant => ({
        value: tenant.id,
        label:
          tenant.id === activeTenantId ? (
            <div className="flex w-full items-center justify-between gap-3">
              <span className="truncate">{tenant.name}</span>
              <span className="flex shrink-0 items-center gap-4">
                <img src={checkIcon} alt="active" />
                <span
                  role="button"
                  tabIndex={0}
                  onClick={event => {
                    event.stopPropagation();
                    window.dispatchEvent(new CustomEvent('app:open-organization-settings'));
                  }}
                  onKeyDown={event => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      event.stopPropagation();
                      window.dispatchEvent(new CustomEvent('app:open-organization-settings'));
                    }
                  }}
                  aria-label={`Open ${tenant.name} settings`}
                  className="shrink-0 cursor-pointer text-gray-900 transition-opacity hover:opacity-80"
                >
                  <Settings className="h-6 w-6" strokeWidth={2} />
                </span>
              </span>
            </div>
          ) : (
            tenant.name
          ),
        labelCurrent: tenant.name,
        hideSelectedCheck: tenant.id === activeTenantId,
        onClick: () => {
          void handleTenantChange(tenant.id);
        },
      })),
    [activeTenantId, availableTenants, handleTenantChange],
  );

  const headerMenuSelectedItem = useMemo(
    () => headerMenuItems.find(item => item.value === activeTenant?.id) ?? headerMenuItems[0],
    [activeTenant?.id, headerMenuItems],
  );

  const keyboardShortcutCallbacks = useMemo(
    () => ({
      onShowHelp: () => setShowShortcutsHelp(true),
      onNewLoan: () => {
        navigate('/loans');
        setTimeout(() => window.dispatchEvent(new CustomEvent('app:new-loan')), 100);
      },
      onNewBorrower: () => {
        navigate('/borrowers');
        setTimeout(() => window.dispatchEvent(new CustomEvent('app:new-borrower')), 100);
      },
    }),
    [navigate],
  );

  useKeyboardShortcuts(keyboardShortcutCallbacks);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handler = () => setShowShortcutsHelp(true);
    window.addEventListener('app:show-shortcuts', handler);
    return () => window.removeEventListener('app:show-shortcuts', handler);
  }, []);

  useEffect(() => {
    const handler = () => setOrganizationSettingsOpen(true);
    window.addEventListener('app:open-organization-settings', handler);
    return () => window.removeEventListener('app:open-organization-settings', handler);
  }, []);

  return (
    <div className="flex h-dvh w-full overflow-hidden bg-[#171717]">
      <Sidebar
        user={user}
        avatarUrl={user?.avatarUrl}
        bootId={bootId}
        appSelectorInfo={appSelectorInfo}
        headerMenuItems={headerMenuItems}
        headerMenuSelectedItem={headerMenuSelectedItem}
        disabledHeaderMenu={availableTenants.length === 0}
        onLogout={() => void logout()}
        onOpenShortcuts={() => setShowShortcutsHelp(true)}
        onOpenBugReport={() => window.dispatchEvent(new CustomEvent('app:open-bug-report'))}
        onOpenFeatureRequest={() =>
          window.dispatchEvent(new CustomEvent('app:open-feature-request'))
        }
        profileMenuItems={profileOptions}
      />

      <div className="flex min-w-0 flex-1 flex-col">
        <TopBar
          user={user}
          avatarUrl={user?.avatarUrl}
          bootId={bootId}
          appSelectorInfo={appSelectorInfo}
          headerMenuItems={headerMenuItems}
          headerMenuSelectedItem={headerMenuSelectedItem}
          disabledHeaderMenu={availableTenants.length === 0}
          isOpen={mobileMenuOpen}
          onOpen={() => setMobileMenuOpen(true)}
          onClose={() => setMobileMenuOpen(false)}
          onLogout={() => void logout()}
          onOpenShortcuts={() => setShowShortcutsHelp(true)}
          onOpenBugReport={() => window.dispatchEvent(new CustomEvent('app:open-bug-report'))}
          onOpenFeatureRequest={() =>
            window.dispatchEvent(new CustomEvent('app:open-feature-request'))
          }
          profileMenuItems={profileOptions}
        />

        <main className="min-h-0 flex-1 overflow-hidden bg-white lg:rounded-tl-xl lg:rounded-bl-xl">
          <div className="h-full overflow-auto">
            <Outlet />
          </div>
        </main>
      </div>

      <CommandPalette />
      <KeyboardShortcutsHelp open={showShortcutsHelp} onClose={() => setShowShortcutsHelp(false)} />
      <FeedbackButton />
      <AiChatPanel />
      {activeTenantId ? (
        <OrganizationSettingsModal
          open={organizationSettingsOpen}
          onClose={() => setOrganizationSettingsOpen(false)}
          tenantId={activeTenantId}
          initialValues={organizationSettings}
        />
      ) : null}
    </div>
  );
}

export default Wrapper;
